import { useState, type ChangeEvent } from 'react';
import { InputProps, Input } from 'antd';
import './index.scss';

/** R + thousands separators + up to 2 decimal places (ex VAT style). */
export function formatContractValueDecimal(displayValue: string): string {
    if (!displayValue) return '';
    const normalized = displayValue.replace(/[,\s]/g, '');
    let s = normalized.replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    let intRaw = firstDot === -1 ? s : s.slice(0, firstDot);
    let decRaw = firstDot === -1 ? '' : s.slice(firstDot + 1).replace(/\./g, '');
    decRaw = decRaw.slice(0, 2);
    intRaw = intRaw.replace(/\D/g, '');
    if (intRaw.length > 1) {
        intRaw = intRaw.replace(/^0+/, '') || '';
    }

    let intFormatted = intRaw;
    if (intFormatted.length > 0) {
        intFormatted = intFormatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    const hadDot = firstDot !== -1;
    if (hadDot) {
        const intPart = intFormatted === '' ? '0' : intFormatted;
        return intPart + '.' + decRaw;
    }
    return intFormatted;
}

interface FloatInputProps extends Omit<InputProps, 'type'> {
    type?: InputProps['type'] | 'contractValue' | 'contractValueDecimal';
    floatLabel: string;
    isContractValue?: boolean;
    setContractValue?: any;
    contractValue?: string;
    setChanged?: React.Dispatch<React.SetStateAction<boolean>>;
    name?: string;
    error?: boolean;
    onBlur?: any;
    maxLength?: number;
    hasError?: () => boolean;
}

export const FloatInput = (props: FloatInputProps) => {
    const [focus, setFocus] = useState(false);
    let {
        floatLabel,
        placeholder,
        className,
        disabled,
        isContractValue = false,
        contractValue,
        setContractValue,
        setChanged,
        name,
        error,
        onBlur,
        maxLength = 11,
        hasError,

        ...restProps
    } = props;
    if (!placeholder) placeholder = floatLabel;

    const isOccupied = focus || props.value || contractValue;

    const labelClass = isOccupied ? 'label as-label' : 'label as-placeholder';

    const requiredMark = props.required ? <span className="red">*</span> : null;

    const currencyMask = (e: any) => {
        let value = e.target.value;
        value = value?.replace(/\D/g, '');
        value = value?.replace(/(\d)(\d{3})$/, '$1,$2');
        value = value?.replace(/(?=(\d{3})+(\D))\B/g, ',');
        e.target.value = value;
        return e.target.value;
    };

    return (
        <div className="float-label" onBlur={() => setFocus(false)} onFocus={() => setFocus(true)}>
            {props.type === 'password' ? (
                <Input.Password {...restProps} className={className} onBlur={onBlur} />
            ) : props.type === 'contractValue' ? (
                <>
                    {(contractValue || focus) && <div className="R">R</div>}
                    <Input
                        {...restProps}
                        className={`${className} ${disabled ? 'disable' : ''} ${error ? 'err' : ''} ${
                            hasError && hasError() ? 'err' : ''
                        } contract-value-input`}
                        disabled={disabled}
                        onBlur={onBlur}
                        maxLength={maxLength}
                        value={contractValue}
                        onChange={(e) => {
                            setContractValue(currencyMask(e));
                            setChanged && setChanged(true);
                        }}
                    />
                </>
            ) : props.type === 'contractValueDecimal' ? (
                (() => {
                    const {
                        value: controlledValue,
                        onChange: formOnChange,
                        type: _contractValDecType,
                        ...inputRest
                    } = restProps;
                    const displayValue =
                        contractValue !== undefined && contractValue !== null
                            ? contractValue
                            : (controlledValue as string | undefined) ?? '';
                    const showR = !!(displayValue || focus);
                    return (
                        <>
                            {showR && <div className="R">R</div>}
                            <Input
                                {...inputRest}
                                className={`${className} ${disabled ? 'disable' : ''} ${error ? 'err' : ''} ${
                                    hasError && hasError() ? 'err' : ''
                                } contract-value-input`}
                                disabled={disabled}
                                onBlur={onBlur}
                                maxLength={maxLength}
                                value={displayValue}
                                onChange={(e) => {
                                    const formatted = formatContractValueDecimal(e.target.value);
                                    setContractValue?.(formatted);
                                    setChanged?.(true);
                                    if (formOnChange) {
                                        const ev = { ...e, target: { ...e.target, value: formatted } };
                                        formOnChange(ev as ChangeEvent<HTMLInputElement>);
                                    }
                                }}
                            />
                        </>
                    );
                })()
            ) : (
                <Input
                    {...restProps}
                    className={`${className} ${disabled ? 'disable' : ''} ${error ? 'err' : ''} ${
                        hasError && hasError() ? 'err' : ''
                    }`}
                    disabled={disabled}
                    maxLength={maxLength}
                    onBlur={onBlur}
                />
            )}
            <label className={labelClass}>
                {isOccupied ? floatLabel : placeholder} {requiredMark}
            </label>
            {isContractValue && (focus || contractValue) && (
                <div className="float-label__contract">
                    R
                    <Input maxLength={maxLength} />
                </div>
            )}
        </div>
    );
};
